<template>
  <HideRbac :permissions="['bulkActionDraftProfiles']">
    <JobProfileActionsMenu
      v-if="isStandaloneJobNav"
      :items="menuItems"
      :disabled="!selectedCount"
      :loading="bulkActionLoading"
      @open="handleOpenMenu"
      @select="handleMenuSelect"
    />

    <DraftBulkPublishModal
      :show="showPublishModal"
      :count="selectedCount"
      :loading="bulkActionLoading"
      @close="showPublishModal = false"
      @confirm="handleConfirmPublish"
    />

    <DraftBulkArchiveModal
      :show="showArchiveModal"
      :count="selectedCount"
      :is-unarchive="isArchivedSelection"
      :loading="bulkActionLoading"
      :result="archiveResult"
      @close="handleCloseArchiveModal"
      @confirm="handleConfirmArchive"
    />
  </HideRbac>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import HideRbac from '@/components/rbac/HideRbac.vue';
import JobProfileActionsMenu from '@/components/shared/JobProfileActionsMenu.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useTracking } from '@/stores/tracking/tracking';
import { JP_PAGE_DRAFT_LIBRARY } from '@/helpers/jobProfileLibraryTracking';
import DraftBulkPublishModal from '@/components/library/draftJobProfiles/DraftBulkPublishModal.vue';
import DraftBulkArchiveModal from '@/components/library/draftJobProfiles/DraftBulkArchiveModal.vue';

const props = defineProps({
  selectedCount: { type: Number, default: 0 },
});

const route = useRoute();
const router = useRouter();

const { isStandaloneJobNav } = storeToRefs(useAppAuthData());
const { bulkActionLoading, hasSelectedArchived } = storeToRefs(useJobProfileDrafts());
const {
  bulkPublishSelected,
  bulkArchiveSelected,
  bulkUnarchiveSelected,
} = useJobProfileDrafts();

const showPublishModal = ref(false);
const showArchiveModal = ref(false);
const archiveResult = ref(null);

// When the Status filter is Archived only, the list only has archived drafts.
const isArchivedStatus = computed(() => route.query?.status === 'archived');

// Archived drafts can only be unarchived. Active drafts can be published or archived.
const isArchivedSelection = computed(() => hasSelectedArchived.value || isArchivedStatus.value);

const menuItems = computed(() => (
  isArchivedSelection.value
    ? [{ value: 'unarchive', label: 'Unarchive' }]
    : [
      { value: 'publish', label: 'Publish' },
      { value: 'archive', label: 'Archive' },
    ]
));

const getReloadParams = () => ({
  routeQuery: route.query,
  router,
  page: route.query?.page || 1,
  searchTerm: route.query?.q || '',
  selectedMyJobs: route.query?.owned,
});

const handleOpenMenu = () => {
  useTracking().jpLibOpenedDraftBulkActions({ page: JP_PAGE_DRAFT_LIBRARY });
};

const handleMenuSelect = (value) => {
  if (value === 'publish') {
    showPublishModal.value = true;
  } else if (value === 'archive' || value === 'unarchive') {
    showArchiveModal.value = true;
  }
};

const handleConfirmPublish = async () => {
  const count = props.selectedCount;

  await bulkPublishSelected(getReloadParams());
  showPublishModal.value = false;

  useTracking().jpLibBulkPublishedDrafts({
    page: JP_PAGE_DRAFT_LIBRARY,
    count,
  });
};

const handleCloseArchiveModal = () => {
  showArchiveModal.value = false;
  archiveResult.value = null;
};

const handleConfirmArchive = async () => {
  const isUnarchive = isArchivedSelection.value;

  const counts = isUnarchive
    ? await bulkUnarchiveSelected(getReloadParams())
    : await bulkArchiveSelected(getReloadParams());

  if (isUnarchive) {
    useTracking().jpLibBulkUnarchivedDrafts({
      page: JP_PAGE_DRAFT_LIBRARY,
      count: counts.successCount,
    });
  } else {
    useTracking().jpLibBulkArchivedDrafts({
      page: JP_PAGE_DRAFT_LIBRARY,
      count: counts.successCount,
    });
  }

  // Some drafts were skipped. Keep the window open so the user can read what happened.
  if (counts.failedCount > 0) {
    archiveResult.value = counts;

    return;
  }

  handleCloseArchiveModal();
};
</script>
